/**
 * SST multi-step flow — R7.2.
 * 5 steps + confirmation. Follows C5 pay-flow pattern.
 * Atlas: Services > Start, Stop, or Transfer.
 */
"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  ChevronLeft,
  CheckCircle2,
  Shield,
  Upload,
  Calendar,
  MapPin,
  ArrowRight,
  ClipboardCheck,
} from "lucide-react";
import { useActivePersona } from "@/lib/persona";
import { useT } from "@/lib/locale";
import { generateSRNumber, useDemoSessionStore } from "@petci/shared-ui";

type Step = "type" | "address" | "date" | "documents" | "authorize" | "confirmation";
const STEPS: Step[] = ["type", "address", "date", "documents", "authorize", "confirmation"];
// Step labels are keyed (D1); the English SSoT lives in services.json.
const STEP_LABEL_KEYS: Record<Step, string> = {
  type: "services.sstStepType",
  address: "services.sstStepAddress",
  date: "services.sstStepDate",
  documents: "services.sstStepDocuments",
  authorize: "services.sstStepAuthorize",
  confirmation: "services.sstStepConfirmation",
};

export function SSTStepClient({ step }: { step: string }) {
  const router = useRouter();
  const persona = useActivePersona();
  const t = useT();
  const currentStep = (STEPS.includes(step as Step) ? step : "type") as Step;
  const stepIndex = STEPS.indexOf(currentStep);

  const [requestType, setRequestType] = useState<"start" | "stop" | "transfer">("transfer");
  const [authorized, setAuthorized] = useState(false);
  const [srNumber] = useState(generateSRNumber);
  const [docUploaded, setDocUploaded] = useState(false);
  const addRequest = useDemoSessionStore((s) => s.addRequest);

  // English enum values stored on the request (SSoT for SR_TYPE_KEYS resolution
  // + the persisted summary) — NOT display copy, so they stay verbatim.
  const REQUEST_TYPE_LABELS = {
    start: "Start Service",
    stop: "Stop Service",
    transfer: "Transfer Service",
  } as const;

  function goTo(s: Step) {
    router.push(`/services/start-stop-transfer/${s}`);
  }

  function handleBack() {
    if (stepIndex > 0) {
      const prev = STEPS[stepIndex - 1];
      if (prev) goTo(prev);
    } else {
      router.push("/services");
    }
  }

  function handleNext() {
    const next = STEPS[stepIndex + 1];
    if (next) goTo(next);
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {/* Back link */}
      {currentStep !== "confirmation" && (
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-sm text-brand-primary-text hover:underline py-1 -my-1"
        >
          <ChevronLeft size={16} aria-hidden />
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
        {stepIndex === 0 ? t("services.allServices") : t(STEP_LABEL_KEYS[STEPS[stepIndex - 1]!])}
        </button>
      )}

      {/* Step indicator */}
      {currentStep !== "confirmation" && (
        <div className="flex items-center gap-1">
          {STEPS.slice(0, 5).map((s, i) => (
            <div key={s} className="flex items-center gap-1 flex-1">
              <div
                className={`h-1.5 flex-1 rounded-full transition-colors duration-base ${
                  i <= stepIndex ? "bg-brand-primary" : "bg-neutral-100"
                }`}
              />
            </div>
          ))}
        </div>
      )}

      <div className="card-surface space-y-5">
        {/* Step 1: Type */}
        {currentStep === "type" && (
          <>
            <div>
              <h1 className="text-xl font-display font-bold text-neutral-900">
                {t("services.sstTypeTitle")}
              </h1>
              <p className="text-sm text-neutral-500 mt-1">
                {t("services.sstTypeSubtitle")}
              </p>
            </div>
            <div className="space-y-3">
              {([
                { value: "start", labelKey: "services.sstOptStartLabel", descKey: "services.sstOptStartDesc" },
                { value: "stop", labelKey: "services.sstOptStopLabel", descKey: "services.sstOptStopDesc" },
                { value: "transfer", labelKey: "services.sstOptTransferLabel", descKey: "services.sstOptTransferDesc" },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRequestType(opt.value)}
                  className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-colors duration-fast ${
                    requestType === opt.value
                      ? "border-brand-primary bg-brand-primary/5"
                      : "border-neutral-200 hover:border-neutral-300"
                  }`}
                >
                  <p className="text-sm font-semibold text-neutral-900">{t(opt.labelKey)}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">{t(opt.descKey)}</p>
                </button>
              ))}
            </div>
            <button onClick={handleNext} className="btn-primary w-full justify-center">
              {t("services.continue")} <ArrowRight size={16} aria-hidden />
            </button>
          </>
        )}

        {/* Step 2: Address */}
        {currentStep === "address" && (
          <>
            <div className="flex items-center gap-3">
              <MapPin size={20} className="text-brand-primary-text" aria-hidden />
              <div>
                <h1 className="text-xl font-display font-bold text-neutral-900">
                  {t("services.serviceAddress")}
                </h1>
                <p className="text-sm text-neutral-500 mt-0.5">
                  {requestType === "start"
                    ? t("services.sstAddressSubStart")
                    : t("services.sstAddressSubConfirm")}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-sm font-medium text-neutral-700">{t("services.streetAddress")}</span>
                <input
                  type="text"
                  defaultValue={requestType === "start" ? "" : persona.serviceAddress.line1}
                  className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
                  placeholder={t("services.sstStreetPlaceholder")}
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-neutral-700">{t("services.districtArea")}</span>
                <input
                  type="text"
                  defaultValue={requestType === "start" ? "" : persona.serviceAddress.district}
                  className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
                  placeholder={t("services.sstDistrictPlaceholder")}
                />
              </label>
              {requestType === "transfer" && (
                <div className="rounded-lg bg-brand-primary/5 border border-brand-primary/20 p-3 space-y-2">
                  <p className="text-xs font-semibold text-brand-primary-text uppercase tracking-wider">
                    {t("services.sstNewAddress")}
                  </p>
                  <label className="block">
                    <span className="text-sm font-medium text-neutral-700">{t("services.streetAddress")}</span>
                    <input
                      type="text"
                      className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
                      placeholder={t("services.sstNewStreetPlaceholder")}
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-neutral-700">{t("services.districtArea")}</span>
                    <input
                      type="text"
                      className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
                      placeholder={t("services.sstNewDistrictPlaceholder")}
                    />
                  </label>
                </div>
              )}
            </div>
            <button onClick={handleNext} className="btn-primary w-full justify-center">
              {t("services.continue")} <ArrowRight size={16} aria-hidden />
            </button>
          </>
        )}

        {/* Step 3: Effective Date */}
        {currentStep === "date" && (
          <>
            <div className="flex items-center gap-3">
              <Calendar size={20} className="text-brand-primary-text" aria-hidden />
              <div>
                <h1 className="text-xl font-display font-bold text-neutral-900">
                  {t("services.sstStepDate")}
                </h1>
                <p className="text-sm text-neutral-500 mt-0.5">
                  {t("services.sstDateSubtitle")}
                </p>
              </div>
            </div>
            <label className="block">
              <span className="text-sm font-medium text-neutral-700">{t("services.sstRequestedDate")}</span>
              <input
                type="date"
                defaultValue="2026-05-14"
                min="2026-05-13"
                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
              />
            </label>
            <p className="text-xs text-neutral-500">
              {t("services.sstDateNote")}
            </p>
            <button onClick={handleNext} className="btn-primary w-full justify-center">
              {t("services.continue")} <ArrowRight size={16} aria-hidden />
            </button>
          </>
        )}

        {/* Step 4: Documents */}
        {currentStep === "documents" && (
          <>
            <div className="flex items-center gap-3">
              <Upload size={20} className="text-brand-primary-text" aria-hidden />
              <div>
                <h1 className="text-xl font-display font-bold text-neutral-900">
                  {t("services.sstDocsTitle")}
                </h1>
                <p className="text-sm text-neutral-500 mt-0.5">
                  {t("services.sstDocsSubtitle")}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {docUploaded ? (
                <div className="rounded-lg border border-semantic-success/30 bg-semantic-success/5 p-4 flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-semantic-success-text" aria-hidden />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">{t("services.sstDocFilename")}</p>
                    <p className="text-xs text-neutral-500">{t("services.sstDocSize")}</p>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setDocUploaded(true)}
                  className="w-full rounded-lg border-2 border-dashed border-neutral-300 p-6 text-center hover:border-brand-primary hover:bg-brand-primary/5 transition-colors duration-fast"
                >
                  <Upload size={24} className="text-neutral-500 mx-auto mb-2" aria-hidden />
                  <p className="text-sm font-medium text-neutral-700">
                    {t("services.sstDocPrompt")}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {t("services.sstDocFormats")}
                  </p>
                </button>
              )}
            </div>
            <button onClick={handleNext} className="btn-primary w-full justify-center">
              {t("services.continue")} <ArrowRight size={16} aria-hidden />
            </button>
          </>
        )}

        {/* Step 5: Authorization */}
        {currentStep === "authorize" && (
          <>
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-brand-primary-text" aria-hidden />
              <div>
                <h1 className="text-xl font-display font-bold text-neutral-900">
                  {t("services.sstStepAuthorize")}
                </h1>
                <p className="text-sm text-neutral-500 mt-0.5">
                  {t("services.sstAuthSubtitle")}
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-neutral-50 p-4 space-y-2 text-sm text-neutral-700">
              <p>
                <strong>{t("services.sstSummaryRequest")}</strong>{" "}
                {requestType === "start"
                  ? t("services.sstOptStartLabel")
                  : requestType === "stop"
                    ? t("services.sstReqStop")
                    : t("services.sstOptTransferLabel")}
              </p>
              <p><strong>{t("services.sstSummaryAccount")}</strong> #{persona.accountNumber}</p>
              <p><strong>{t("services.sstSummaryAddress")}</strong> {persona.serviceAddress.line1}</p>
              <p><strong>{t("services.sstSummaryEffective")}</strong> {t("services.sstEffectiveValue")}</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={authorized}
                onChange={(e) => setAuthorized(e.target.checked)}
                className="mt-0.5 rounded border-neutral-300 text-brand-primary-text focus:ring-brand-primary"
              />
              <span className="text-xs text-neutral-700 leading-relaxed">
                {t("services.sstAuthConfirm")}
              </span>
            </label>

            <button
              onClick={() => {
                addRequest(persona.id, {
                  id: srNumber,
                  type: REQUEST_TYPE_LABELS[requestType],
                  status: {
                    state: "received",
                    receivedAt: "2026-05-10",
                  },
                  summary: `${REQUEST_TYPE_LABELS[requestType]} — ${persona.serviceAddress.line1}`,
                });
                handleNext();
              }}
              disabled={!authorized}
              className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ClipboardCheck size={16} aria-hidden />
              {t("services.sstSubmit")}
            </button>
          </>
        )}

        {/* Step 6: Confirmation */}
        {currentStep === "confirmation" && (
          <div className="text-center space-y-4 py-4">
            <div className="w-16 h-16 rounded-full bg-semantic-success/10 flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} className="text-semantic-success-text" aria-hidden />
            </div>
            <div>
              <h1 className="text-xl font-display font-bold text-neutral-900">
                {t("services.sstConfirmTitle")}
              </h1>
              <p className="text-sm text-neutral-500 mt-1">
                {t("services.sstConfirmSubtitle")}
              </p>
            </div>

            <div className="rounded-lg bg-neutral-50 p-4 space-y-1">
              <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">
                {t("services.sstReferenceNumber")}
              </p>
              <p className="text-lg font-mono font-bold text-brand-primary-text">
                {srNumber}
              </p>
              <p className="text-xs text-neutral-500">
                {t("services.sstExpectedCompletion")}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Link
                href="/services/track"
                className="btn-primary w-full justify-center"
              >
                {t("services.trackTitle")}
              </Link>
              <Link
                href="/home"
                className="btn-secondary w-full justify-center"
              >
                {/* D-8: a completion CTA on the success screen, so it takes the
                    CTA form rather than the chevron form. The services-local key
                    it used to call was a capitalization-only duplicate of this one
                    ("Back to Home" vs "Back to home") and has been deleted. */}
                {t("common.backToHome")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
